import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import { findModelManifest } from "@bindery/shared/corpus/passages";
import {
  vocabularyProfileShape,
  withParallelPredictions,
  wordIdsSchema,
} from "@bindery/shared/corpus/selection";
import {
  AWRF_CANDIDATE_TEXTS,
  AWRF_CANDIDATE_TEXTS_MAX,
} from "@bindery/shared/reader/constants/awrf-config";
import * as z from "zod";
import { ORIENTATION_API_PREFIX } from "./errors";

export const AWRF_CANDIDATES_PATH = `${ORIENTATION_API_PREFIX}/awrf/candidates`;

// selectReadings' own `limit` cap.
const MAX_CANDIDATE_TEXTS = 100;
// A 60 s list, 96 adaptive words and an ORF passage fit many times over;
// the cap bounds cost, not the protocol.
const MAX_WORDS = 2000;
const MAX_WORD_LENGTH = 64;

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);
const wordsSchema = z.array(z.string().max(MAX_WORD_LENGTH)).max(MAX_WORDS);
const textCountSchema = z.number().int().min(1).max(MAX_CANDIDATE_TEXTS);

export const awrfCandidatesRequestSchema = withParallelPredictions(
  z.strictObject({
    sessionId: z.string().regex(UUID_REGEX),
    ...vocabularyProfileShape,
    /** Word ids the child missed, skipped or hesitated on (#168's −1). */
    unknownWordIds: wordIdsSchema.default([]),
    /** The manifest the browser predicts with; its layout is the one the store must hold. */
    modelManifest: z.strictObject({
      version: z.number().int().positive(),
      sha256: sha256Schema,
    }),
    /** Every word aWRF has scored this run, known or not (Q14). */
    observedWords: wordsSchema,
    /** Words on the list the child has not reached yet. */
    queuedWords: wordsSchema.default([]),
    /** The ORF passage's words; folded server-side. */
    orfWords: wordsSchema,
    /** Eligible words below which the route widens the candidate texts. */
    minCandidates: z.number().int().min(1).max(MAX_WORDS),
    candidateTexts: textCountSchema.default(AWRF_CANDIDATE_TEXTS),
    candidateTextsMax: textCountSchema.default(AWRF_CANDIDATE_TEXTS_MAX),
  }),
)
  .refine((data) => findModelManifest(data.modelManifest) !== undefined, {
    message: "modelManifest is not a recorded manifest (version and sha256)",
    path: ["modelManifest"],
  })
  .refine((data) => data.candidateTexts <= data.candidateTextsMax, {
    message: "candidateTexts must not exceed candidateTextsMax",
    path: ["candidateTexts"],
  });
export type AwrfCandidatesRequest = z.input<typeof awrfCandidatesRequestSchema>;

export const awrfCandidatesResponseSchema = z.object({
  /** Model index (store word_id − 1) and token count across the texts; unique, ascending. */
  candidates: z.array(
    z.object({
      index: z.number().int().min(0),
      tokens: z.number().int().min(1),
    }),
  ),
  /** The texts the vocabulary came from, best first. */
  textIds: z.array(z.string()),
  /** The k the answer was built from, after any widening. */
  candidateTexts: z.number().int().min(1),
  /** The store generation read, which holds the model's layout. */
  vocabVersion: z.string(),
  vocabLayoutSha256: sha256Schema,
});
export type AwrfCandidatesResponse = z.infer<
  typeof awrfCandidatesResponseSchema
>;
